/**
 * useBoardRealtime Hook
 *
 * Manages real-time synchronization for board data via WebSocket.
 * Handles event processing, optimistic updates, and conflict resolution.
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useBoardStore } from '../stores/boardStore';
import {
  RealtimeClient,
  createRealtimeClient,
  getRealtimeClient,
  destroyRealtimeClient,
  type ConnectionState,
  type EventMessage,
  type BoardEventType,
  type GroupCreatedPayload,
  type GroupUpdatedPayload,
  type GroupDeletedPayload,
  type GroupReorderedPayload,
  type ColumnCreatedPayload,
  type ColumnUpdatedPayload,
  type ColumnDeletedPayload,
  type ColumnReorderedPayload,
  type ItemCreatedPayload,
  type ItemUpdatedPayload,
  type ItemDeletedPayload,
  type ItemReorderedPayload,
  type ItemMovedPayload,
  type ColumnValueChangedPayload,
} from '../lib/realtime';

// Configuration - would come from environment in production
const REALTIME_URL = import.meta.env.VITE_REALTIME_URL || 'wss://api.wmp.io/v1/ws';

// Pending operation for optimistic updates
interface PendingOperation {
  id: string;
  type: 'item' | 'group' | 'column' | 'value';
  action: 'create' | 'update' | 'delete' | 'move' | 'reorder';
  entityId: string;
  previousState?: unknown;
  timestamp: number;
}

interface UseBoardRealtimeOptions {
  boardId: string;
  enabled?: boolean;
  onConnectionChange?: (state: ConnectionState) => void;
  onError?: (error: { code: number; message: string }) => void;
}

interface UseBoardRealtimeReturn {
  connectionState: ConnectionState;
  isConnected: boolean;
  pendingOperations: number;
  connect: () => void;
  disconnect: () => void;
  subscribeToItem: (itemId: string) => void;
  unsubscribeFromItem: (itemId: string) => void;
}

/**
 * Hook for real-time board synchronization
 */
export function useBoardRealtime({
  boardId,
  enabled = true,
  onConnectionChange,
  onError,
}: UseBoardRealtimeOptions): UseBoardRealtimeReturn {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [pendingOps, setPendingOps] = useState<PendingOperation[]>([]);

  const clientRef = useRef<RealtimeClient | null>(null);
  const pendingOpsRef = useRef<Map<string, PendingOperation>>(new Map());
  const currentUserIdRef = useRef<string | null>(null);

  // Store actions
  const addItem = useBoardStore((state) => state.addItem);
  const updateItem = useBoardStore((state) => state.updateItem);
  const deleteItem = useBoardStore((state) => state.deleteItem);
  const moveItem = useBoardStore((state) => state.moveItem);
  const reorderItem = useBoardStore((state) => state.reorderItem);
  const updateItemValue = useBoardStore((state) => state.updateItemValue);
  const addGroup = useBoardStore((state) => state.addGroup);
  const updateGroup = useBoardStore((state) => state.updateGroup);
  const deleteGroup = useBoardStore((state) => state.deleteGroup);
  const reorderGroup = useBoardStore((state) => state.reorderGroup);
  const addColumn = useBoardStore((state) => state.addColumn);
  const updateColumn = useBoardStore((state) => state.updateColumn);
  const deleteColumn = useBoardStore((state) => state.deleteColumn);
  const reorderColumn = useBoardStore((state) => state.reorderColumn);
  const board = useBoardStore((state) => state.board);

  /**
   * Process incoming board events
   */
  const handleBoardEvent = useCallback(
    (event: EventMessage) => {
      const eventType = event.event as BoardEventType;
      const payload = event.payload;

      // Skip events from current user if we have a pending operation
      // This prevents duplicate updates from optimistic updates
      const userId = (payload as { updated_by?: string; created_by?: string; deleted_by?: string; moved_by?: string })
        .updated_by ||
        (payload as { created_by?: string }).created_by ||
        (payload as { deleted_by?: string }).deleted_by ||
        (payload as { moved_by?: string }).moved_by;

      if (userId === currentUserIdRef.current) {
        // Check if we have a pending operation for this
        const pendingKey = getPendingKey(eventType, payload);
        if (pendingKey && pendingOpsRef.current.has(pendingKey)) {
          // Remove the pending operation - server confirmed
          pendingOpsRef.current.delete(pendingKey);
          setPendingOps(Array.from(pendingOpsRef.current.values()));
          return;
        }
      }

      // Process the event
      switch (eventType) {
        case 'group.created': {
          const p = payload as GroupCreatedPayload;
          // Only add if we don't have this group already (optimistic update case)
          const existingGroup = board?.groups.find((g) => g.id === p.group.id);
          if (!existingGroup) {
            addGroup(p.group.name, p.group.color);
          }
          break;
        }

        case 'group.updated': {
          const p = payload as GroupUpdatedPayload;
          updateGroup(p.group_id, p.changes);
          break;
        }

        case 'group.deleted': {
          const p = payload as GroupDeletedPayload;
          deleteGroup(p.group_id);
          break;
        }

        case 'group.reordered': {
          const p = payload as GroupReorderedPayload;
          // Find the group at old position to get its overId
          const groups = board?.groups.sort((a, b) => a.position - b.position);
          const targetGroup = groups?.[p.new_position];
          if (targetGroup && targetGroup.id !== p.group_id) {
            reorderGroup(p.group_id, targetGroup.id);
          }
          break;
        }

        case 'column.created': {
          const p = payload as ColumnCreatedPayload;
          const existingColumn = board?.columns.find((c) => c.id === p.column.id);
          if (!existingColumn) {
            addColumn(p.column.name, p.column.type);
          }
          break;
        }

        case 'column.updated':
        case 'column.resized': {
          const p = payload as ColumnUpdatedPayload;
          updateColumn(p.column_id, p.changes);
          break;
        }

        case 'column.deleted': {
          const p = payload as ColumnDeletedPayload;
          deleteColumn(p.column_id);
          break;
        }

        case 'column.reordered': {
          const p = payload as ColumnReorderedPayload;
          const columns = board?.columns.sort((a, b) => a.position - b.position);
          const targetColumn = columns?.[p.new_position];
          if (targetColumn && targetColumn.id !== p.column_id) {
            reorderColumn(p.column_id, targetColumn.id);
          }
          break;
        }

        case 'item.created': {
          const p = payload as ItemCreatedPayload;
          // Check if item already exists (optimistic update)
          let itemExists = false;
          for (const group of board?.groups || []) {
            if (group.items.find((i) => i.id === p.item.id)) {
              itemExists = true;
              break;
            }
          }
          if (!itemExists) {
            addItem(p.item.group_id, p.item.name);
          }
          break;
        }

        case 'item.updated': {
          const p = payload as ItemUpdatedPayload;
          updateItem(p.item_id, p.changes);
          break;
        }

        case 'item.deleted': {
          const p = payload as ItemDeletedPayload;
          deleteItem(p.item_id);
          break;
        }

        case 'item.reordered': {
          const p = payload as ItemReorderedPayload;
          const group = board?.groups.find((g) => g.id === p.group_id);
          const items = group?.items.sort((a, b) => a.position - b.position);
          const targetItem = items?.[p.new_position];
          if (targetItem && targetItem.id !== p.item_id) {
            reorderItem(p.item_id, targetItem.id, p.group_id);
          }
          break;
        }

        case 'item.moved': {
          const p = payload as ItemMovedPayload;
          moveItem(p.item_id, p.to_group_id, p.position);
          break;
        }

        case 'column_value.changed': {
          const p = payload as ColumnValueChangedPayload;
          updateItemValue(p.item_id, p.column_id, p.value);
          break;
        }

        case 'board.updated': {
          // Board metadata update - would need to update board name/description
          // For now just trigger a refresh
          console.log('[useBoardRealtime] Board updated:', payload);
          break;
        }

        default:
          console.log('[useBoardRealtime] Unhandled event:', eventType, payload);
      }
    },
    [
      board,
      addItem,
      updateItem,
      deleteItem,
      moveItem,
      reorderItem,
      updateItemValue,
      addGroup,
      updateGroup,
      deleteGroup,
      reorderGroup,
      addColumn,
      updateColumn,
      deleteColumn,
      reorderColumn,
    ]
  );

  /**
   * Handle events from the WebSocket
   */
  const handleEvent = useCallback(
    (event: EventMessage) => {
      const room = event.room;

      // Board events
      if (room === `board:${boardId}`) {
        handleBoardEvent(event);
      }

      // Item events (for item detail panels)
      if (room.startsWith('item:')) {
        // These are update/reply/file events - would be handled separately
        console.log('[useBoardRealtime] Item event:', event);
      }

      // User events (notifications)
      if (room.startsWith('user:')) {
        console.log('[useBoardRealtime] User event:', event);
      }
    },
    [boardId, handleBoardEvent]
  );

  /**
   * Initialize and manage the WebSocket connection
   */
  useEffect(() => {
    if (!enabled || !boardId) {
      return;
    }

    // Get or create the client
    let client = getRealtimeClient();
    if (!client) {
      // In production, would get token from auth context
      const token = localStorage.getItem('access_token') || 'demo-token';
      client = createRealtimeClient({
        url: REALTIME_URL,
        token,
      });
      clientRef.current = client;
    }

    // Register handlers
    const unsubscribeEvent = client.onEvent(handleEvent);
    const unsubscribeConnection = client.onConnectionChange((state) => {
      setConnectionState(state);
      onConnectionChange?.(state);
    });
    const unsubscribeError = client.onError((error) => {
      console.error('[useBoardRealtime] Error:', error);
      onError?.(error);
    });

    // Connect and subscribe to board room
    client.connect();
    client.subscribe(`board:${boardId}`);

    // Cleanup
    return () => {
      unsubscribeEvent();
      unsubscribeConnection();
      unsubscribeError();
      client?.unsubscribe(`board:${boardId}`);
    };
  }, [boardId, enabled, handleEvent, onConnectionChange, onError]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Don't destroy the client on unmount - it may be used by other components
      // Only disconnect if this is the last subscriber
    };
  }, []);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    const client = getRealtimeClient();
    client?.connect();
  }, []);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    destroyRealtimeClient();
    setConnectionState('disconnected');
  }, []);

  /**
   * Subscribe to item-specific events
   */
  const subscribeToItem = useCallback((itemId: string) => {
    const client = getRealtimeClient();
    client?.subscribe(`item:${itemId}`);
  }, []);

  /**
   * Unsubscribe from item-specific events
   */
  const unsubscribeFromItem = useCallback((itemId: string) => {
    const client = getRealtimeClient();
    client?.unsubscribe(`item:${itemId}`);
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    pendingOperations: pendingOps.length,
    connect,
    disconnect,
    subscribeToItem,
    unsubscribeFromItem,
  };
}

/**
 * Generate a key for pending operations to match with incoming events
 */
function getPendingKey(eventType: string, payload: unknown): string | null {
  const p = payload as Record<string, unknown>;

  switch (eventType) {
    case 'item.created':
      return `item:create:${(p.item as { id: string })?.id}`;
    case 'item.updated':
      return `item:update:${p.item_id}`;
    case 'item.deleted':
      return `item:delete:${p.item_id}`;
    case 'item.moved':
      return `item:move:${p.item_id}`;
    case 'item.reordered':
      return `item:reorder:${p.item_id}`;
    case 'group.created':
      return `group:create:${(p.group as { id: string })?.id}`;
    case 'group.updated':
      return `group:update:${p.group_id}`;
    case 'group.deleted':
      return `group:delete:${p.group_id}`;
    case 'group.reordered':
      return `group:reorder:${p.group_id}`;
    case 'column.created':
      return `column:create:${(p.column as { id: string })?.id}`;
    case 'column.updated':
    case 'column.resized':
      return `column:update:${p.column_id}`;
    case 'column.deleted':
      return `column:delete:${p.column_id}`;
    case 'column.reordered':
      return `column:reorder:${p.column_id}`;
    case 'column_value.changed':
      return `value:update:${p.item_id}:${p.column_id}`;
    default:
      return null;
  }
}

/**
 * Hook to track optimistic updates
 * Use this to register pending operations before making API calls
 */
export function useOptimisticUpdate() {
  const addPendingOperation = useCallback(
    (
      type: PendingOperation['type'],
      action: PendingOperation['action'],
      entityId: string,
      _previousState?: unknown
    ): string => {
      const id = `${type}:${action}:${entityId}`;
      // Would store in a ref or zustand store for tracking
      console.log('[useOptimisticUpdate] Added pending:', id);
      return id;
    },
    []
  );

  const removePendingOperation = useCallback((id: string) => {
    console.log('[useOptimisticUpdate] Removed pending:', id);
  }, []);

  const rollbackOperation = useCallback((id: string, previousState: unknown) => {
    console.log('[useOptimisticUpdate] Rolling back:', id, previousState);
    // Would restore previous state from the store
  }, []);

  return {
    addPendingOperation,
    removePendingOperation,
    rollbackOperation,
  };
}

/**
 * Hook for presence updates (cursor position, viewing status)
 */
export function useBoardPresence(boardId: string) {
  const updatePresence = useCallback(
    (data: { cursor?: { x: number; y: number }; viewing?: { item_id?: string } }) => {
      const client = getRealtimeClient();
      if (client) {
        client.updatePresence(`board:${boardId}`, {
          ...data,
          status: 'online',
        });
      }
    },
    [boardId]
  );

  return { updatePresence };
}
